/** @format */

import { AxiosError } from "axios";
import { API_ENDPOINTS } from "../constants/api";
import {
  TIntermittentAction,
  TIntermittentCommunicationDetail,
  TIntermittentFilters,
  TIntermittentList,
  TIntermittentSummary,
} from "../redux-modules/declarations/maps/intermittentCommunications";
import apiClient from "../utils/apiClient";

/** Rotta backend per ogni azione manuale. */
const ACTION_PATH: Record<TIntermittentAction, string> = {
  confirm: "confirm",
  send_now: "send-now",
  exclude: "exclude",
  restore: "restore",
  cancel_now: "cancel-now",
  mark_worked: "mark-worked",
  retry: "retry",
  mark_sent: "mark-sent",
};

/** Messaggio leggibile da un errore axios (usa il "message" restituito dal backend). */
export const intermittentErrorMessage = (err: unknown, fallback: string): string => {
  const axiosError = err as AxiosError<{ message?: string }>;
  return axiosError?.response?.data?.message || fallback;
};

export const apiIntermittentList = async (
  filters: TIntermittentFilters,
): Promise<TIntermittentList> => {
  const params: Record<string, string | number> = {
    page: filters.page || 1,
    per_page: filters.per_page || 20,
    sort: filters.sort || "desc",
  };
  if (filters.status && filters.status.length) params.status = filters.status.join(",");
  if (filters.needs_action) params.needs_action = 1;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.search) params.search = filters.search;
  if (filters.worker_type) params.worker_type = filters.worker_type;

  const res = await apiClient.get(API_ENDPOINTS.INTERMITTENT_COMMUNICATIONS, { params });

  return {
    data: res.data.data,
    pagination: {
      current: res.data.meta.current_page,
      pageSize: res.data.meta.per_page,
      total: res.data.meta.total,
    },
  };
};

export const apiIntermittentSummary = async (): Promise<TIntermittentSummary> => {
  const res = await apiClient.get(API_ENDPOINTS.INTERMITTENT_COMMUNICATIONS_SUMMARY);
  return res.data;
};

export const apiIntermittentDetail = async (
  id: number,
): Promise<TIntermittentCommunicationDetail> => {
  const res = await apiClient.get(API_ENDPOINTS.INTERMITTENT_COMMUNICATION.replace(":id", "" + id));
  return res.data.data;
};

export const apiIntermittentAction = async (
  id: number,
  action: TIntermittentAction,
  note?: string,
): Promise<TIntermittentCommunicationDetail> => {
  const url = API_ENDPOINTS.INTERMITTENT_COMMUNICATION_ACTION.replace(":id", "" + id).replace(
    ":action",
    ACTION_PATH[action],
  );
  const res = await apiClient.post(url, note ? { note } : {});
  return res.data.data;
};

/** Inserisce/corregge il codice INPS nell'anagrafica del dipendente; restituisce la comunicazione aggiornata. */
export const apiIntermittentUpdateInpsCode = async (
  id: number,
  inpsCode: string,
): Promise<TIntermittentCommunicationDetail> => {
  const res = await apiClient.put(API_ENDPOINTS.INTERMITTENT_COMMUNICATION_INPS_CODE.replace(":id", "" + id), {
    inps_code: inpsCode,
  });
  return res.data.data;
};

/** Primo messaggio di validazione del backend (errori 422), altrimenti il "message". */
export const intermittentValidationMessage = (err: unknown, fallback: string): string => {
  const axiosError = err as AxiosError<{ message?: string; errors?: Record<string, string[]> }>;
  const errors = axiosError?.response?.data?.errors;
  const first = errors ? Object.values(errors)[0] : undefined;
  return (first && first[0]) || intermittentErrorMessage(err, fallback);
};

/**
 * XML inviato, oppure anteprima generata dai dati salvati se non è ancora partito.
 * Il nome del file si ricava dalla comunicazione (gli header di risposta non sono
 * esposti dal CORS del backend).
 */
export const apiIntermittentXml = async (id: number, type: "send" | "cancel"): Promise<string> => {
  const res = await apiClient.get(API_ENDPOINTS.INTERMITTENT_COMMUNICATION_XML.replace(":id", "" + id), {
    params: { type },
    responseType: "text",
    transformResponse: [(data: string): string => data],
  });
  return res.data;
};
